import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Button, Popconfirm, Tabs, Tooltip } from 'antd';
import { CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  UNSAFE_LocationContext as LocationContext,
  useLocation,
  useNavigationType,
  useOutlet,
} from 'react-router-dom';
import { PageReturnContext, PageTabActiveContext } from '@/hooks/usePageTabActive';
import { PageBackContext } from '@/hooks/usePageBack';
import { pageId, type PageTabView } from './usePageTabs';

type LocationContextValue = React.ContextType<typeof LocationContext>;

interface BarProps {
  tabs: PageTabView[];
  currentKey: string | null;
  onSelect: (key: string) => void;
  onRemove: (key: string) => void;
  onRefresh: (key: string) => void;
  onRemoveAll: () => void;
}

export const PageTabsBar: React.FC<BarProps> = ({ tabs, currentKey, onSelect, onRemove, onRefresh, onRemoveAll }) => (
  <div className="tenant-page-tabs">
    <Tabs
      type="editable-card"
      hideAdd
      size="small"
      activeKey={currentKey ?? ''}
      onChange={onSelect}
      onEdit={(key, action) => {
        if (action === 'remove' && typeof key === 'string') onRemove(key);
      }}
      tabBarExtraContent={{
        right: (
          <Popconfirm title="确定关闭全部标签？" okText="关闭" cancelText="取消" onConfirm={onRemoveAll}>
            <Button type="text" size="small" icon={<CloseCircleOutlined />} disabled={tabs.length <= 1}>
              关闭全部
            </Button>
          </Popconfirm>
        ),
      }}
      items={tabs.map((tab) => ({
        key: tab.key,
        closable: tab.closable,
        label: tab.key === currentKey ? (
          <span className="tenant-page-tab-label">
            {tab.title}
            <Tooltip title="刷新">
              <ReloadOutlined
                className="tenant-page-tab-reload"
                aria-label="刷新"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh(tab.key);
                }}
              />
            </Tooltip>
          </span>
        ) : tab.title,
      }))}
    />
  </div>
);

interface OutletProps {
  currentKey: string | null;
  tabs: PageTabView[];
  versions: Record<string, number>;
  onBack: (key: string) => void;
}

interface CacheEntry {
  tabKey: string;
  element: React.ReactNode;
  locationContext: LocationContextValue;
  back: () => void;
  /** 被本标签里的其他页面盖住后又回到前台的次数 */
  returns: number;
}

/**
 * 每个打开的标签各留一份页面，切换时只隐藏不卸载，页面里的输入、筛选、分页都保留。
 * 在标签里从列表点进详情时，列表页也留着，返回时原样显示。
 * 隐藏的页面冻结在它自己的地址上，别的标签跳转时它不会跟着重新请求。
 */
export const PageTabsOutlet: React.FC<OutletProps> = ({ currentKey, tabs, versions, onBack }) => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigationType = useNavigationType();
  const cache = useRef(new Map<string, CacheEntry>()).current;
  /** 每个标签上次显示的是哪一页 */
  const shownInTab = useRef(new Map<string, string>()).current;

  const visibleId = currentKey ? pageId(currentKey, location.pathname) : null;
  if (currentKey && visibleId) {
    const prev = cache.get(visibleId);
    const returned = prev !== undefined && shownInTab.get(currentKey) !== visibleId;
    shownInTab.set(currentKey, visibleId);
    cache.set(visibleId, {
      tabKey: currentKey,
      element: outlet,
      locationContext: prev?.locationContext.location === location
        ? prev.locationContext
        : { location, navigationType },
      back: prev?.back ?? (() => onBack(currentKey)),
      returns: (prev?.returns ?? 0) + (returned ? 1 : 0),
    });
  }
  const kept = new Set(tabs.flatMap((tab) => tab.pages.map((p) => pageId(tab.key, p))));
  for (const id of cache.keys()) {
    if (id !== visibleId && !kept.has(id)) cache.delete(id);
  }

  // 页面在同一个窗口里滚动，按页面记住各自的位置
  const scrollTops = useRef(new Map<string, number>()).current;
  const scrollKey = useRef(visibleId);
  useEffect(() => {
    const onScroll = () => {
      if (scrollKey.current) scrollTops.set(scrollKey.current, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollTops]);
  useLayoutEffect(() => {
    if (scrollKey.current === visibleId) return;
    scrollKey.current = visibleId;
    window.scrollTo(0, (visibleId && scrollTops.get(visibleId)) || 0);
    // 隐藏期间窗口尺寸变过的话，图表、表格要重新量一次尺寸
    window.dispatchEvent(new Event('resize'));
  }, [visibleId, scrollTops]);

  return (
    <>
      {Array.from(cache, ([id, entry]) => (
        <div key={`${id}:${versions[id] ?? 0}`} style={id === visibleId ? undefined : { display: 'none' }}>
          <PageTabActiveContext.Provider value={id === visibleId}>
            <PageReturnContext.Provider value={entry.returns}>
              <PageBackContext.Provider value={entry.back}>
                <LocationContext.Provider value={entry.locationContext}>{entry.element}</LocationContext.Provider>
              </PageBackContext.Provider>
            </PageReturnContext.Provider>
          </PageTabActiveContext.Provider>
        </div>
      ))}
      {currentKey === null && outlet}
    </>
  );
};
